import React from "react";
import axios from "axios";
import { toast } from "sonner";
import { Package, MapPin, CheckCircle2, XCircle, Clock, Phone, Mail, FileDown, BarChart3, Cpu, Banknote } from "lucide-react";
import PickupAnalyticsTab from "./PickupAnalyticsTab";
import EquipmentAdminTab from "./EquipmentAdminTab";
import PayoutsAdminTab from "./PayoutsAdminTab";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PakkeshopPanel({ token }) {
  const [apps, setApps] = React.useState([]);
  const [partners, setPartners] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [tab, setTab] = React.useState("applications");

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const [appsRes, partnersRes] = await Promise.all([
        axios.get(`${API}/admin/pakkeshop-applications`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/pakkeshop-partners`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      setApps(appsRes.data.applications || []);
      setPartners(partnersRes.data.partners || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [token]);

  React.useEffect(() => { load(); }, [load]);

  const approve = async (app) => {
    if (!confirm(`Godkend ${app.shop_name} som pakkeshop-partner? PIN-kode vil blive sendt på email.`)) return;
    try {
      const { data } = await axios.post(`${API}/admin/pakkeshop-applications/${app.id}/approve`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Godkendt! PIN-kode: ${data.pin}`);
      await load();
    } catch (e) { toast.error("Kunne ikke godkende"); }
  };

  const reject = async (app) => {
    if (!confirm(`Afvis ansøgning fra ${app.shop_name}?`)) return;
    try {
      await axios.post(`${API}/admin/pakkeshop-applications/${app.id}/reject`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Afvist");
      await load();
    } catch (e) { toast.error("Kunne ikke afvise"); }
  };

  const downloadFlyer = async (partner) => {
    try {
      const res = await axios.get(`${API}/admin/pakkeshop-partners/${partner.id}/flyer.pdf`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: "blob",
      });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `EMA-Pakkeshop-${(partner.shop_name || "flyer").replace(/[^a-z0-9]+/gi, "-")}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Flyer downloadet");
    } catch (e) { toast.error("Kunne ikke hente flyer"); }
  };

  const updateCapacity = async (partner, maxParcels) => {
    const val = parseInt(maxParcels, 10);
    if (!val || val < 1) return;
    try {
      await axios.patch(
        `${API}/admin/distribution/pakkeshop/partners/${partner.id}/capacity`,
        { max_parcels: val },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success(`Kapacitet opdateret for ${partner.shop_name}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kunne ikke opdatere kapacitet");
    }
  };

  if (loading) return <div className="text-brand-text2" data-testid="ps-admin-loading">Indlæser pakkeshops...</div>;

  const pending = apps.filter(a => a.status === "submitted");

  return (
    <div data-testid="pakkeshop-admin-panel" className="space-y-5">
      {/* Header */}
      <div className="bg-white border border-brand p-5">
        <div className="flex items-center gap-3">
          <Package size={22} className="text-brand-red" />
          <div className="flex-1">
            <div className="text-xs uppercase tracking-widest text-brand-text2 font-semibold">Pakkeshop · Partner-program</div>
            <div className="font-display text-lg text-brand-dark">Ansøgninger & aktive partnere</div>
          </div>
          <div className="text-right text-sm">
            <div className="font-display font-bold text-2xl text-brand-red">{pending.length}</div>
            <div className="text-brand-text2 text-xs">venter på godkendelse</div>
          </div>
        </div>
      </div>

      {/* Tab toggle */}
      <div className="flex gap-2 flex-wrap">
        <button onClick={() => setTab("applications")} className={`px-4 py-2 text-sm font-semibold border ${tab === "applications" ? "bg-brand-dark text-white border-brand-dark" : "bg-white text-brand-dark border-brand"}`} data-testid="ps-subtab-applications">
          Ansøgninger ({apps.length})
        </button>
        <button onClick={() => setTab("partners")} className={`px-4 py-2 text-sm font-semibold border ${tab === "partners" ? "bg-brand-dark text-white border-brand-dark" : "bg-white text-brand-dark border-brand"}`} data-testid="ps-subtab-partners">
          Aktive partnere ({partners.length})
        </button>
        <button onClick={() => setTab("analytics")} className={`px-4 py-2 text-sm font-semibold border ${tab === "analytics" ? "bg-brand-dark text-white border-brand-dark" : "bg-white text-brand-dark border-brand"}`} data-testid="ps-subtab-analytics">
          <BarChart3 size={12} className="inline mr-1" /> Analytics
        </button>
        <button onClick={() => setTab("equipment")} className={`px-4 py-2 text-sm font-semibold border ${tab === "equipment" ? "bg-brand-dark text-white border-brand-dark" : "bg-white text-brand-dark border-brand"}`} data-testid="ps-subtab-equipment">
          <Cpu size={12} className="inline mr-1" /> Udstyr
        </button>
        <button onClick={() => setTab("payouts")} className={`px-4 py-2 text-sm font-semibold border ${tab === "payouts" ? "bg-brand-dark text-white border-brand-dark" : "bg-white text-brand-dark border-brand"}`} data-testid="ps-subtab-payouts">
          <Banknote size={12} className="inline mr-1" /> Udbetalinger
        </button>
      </div>

      {tab === "analytics" && <PickupAnalyticsTab token={token} />}
      {tab === "equipment" && <EquipmentAdminTab token={token} partners={partners} />}
      {tab === "payouts" && <PayoutsAdminTab token={token} partners={partners} />}

      {tab === "applications" && (
        <div className="space-y-3">
          {apps.length === 0 && (
            <div className="bg-white border border-brand p-8 text-center text-brand-text2">
              Ingen ansøgninger endnu. Del linket <code>emakurer.dk/bliv-pakkeshop</code> med lokale butikker.
            </div>
          )}
          {apps.map((a) => (
            <div key={a.id} className="bg-white border border-brand p-5">
              <div className="flex items-start justify-between flex-wrap gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="font-display text-lg text-brand-dark font-semibold">{a.shop_name}</div>
                    <span className="text-xs bg-brand-bg px-2 py-0.5 capitalize">{a.business_type || "—"}</span>
                    <span className={`text-xs px-2 py-0.5 ${a.status === "submitted" ? "bg-amber-50 text-amber-700" : a.status === "approved" ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>
                      {a.status === "submitted" ? "Afventer" : a.status === "approved" ? "Godkendt" : "Afvist"}
                    </span>
                  </div>
                  <div className="text-sm text-brand-text2 mt-2 flex items-center gap-2">
                    <MapPin size={12} /> {a.shop_address}, {a.shop_zip} {a.shop_city}
                  </div>
                  <div className="text-sm text-brand-text2 mt-1 flex items-center gap-2">
                    <Clock size={12} /> {a.opening_hours}
                  </div>
                  <div className="text-sm text-brand-text2 mt-1 flex items-center gap-3 flex-wrap">
                    <span className="flex items-center gap-1"><Phone size={11} /> {a.owner_phone}</span>
                    <span className="flex items-center gap-1"><Mail size={11} /> {a.owner_email}</span>
                  </div>
                  <div className="text-sm text-brand-text2 mt-1">
                    Forventet: <strong>{a.estimated_daily_pickups} pakker/dag</strong> · Plads: <strong>{a.storage_space_sqm} m²</strong>
                    {a.has_camera && " · Kamera ✓"}
                    {a.has_signage_space && " · Skilteplads ✓"}
                  </div>
                  {a.notes && <div className="text-xs bg-brand-bg p-2 mt-2 italic">{a.notes}</div>}
                  {Array.isArray(a.shop_photos) && a.shop_photos.length > 0 && (
                    <div className="mt-3 grid grid-cols-3 sm:grid-cols-6 gap-2" data-testid={`ps-photos-${a.id}`}>
                      {a.shop_photos.map((ph, i) => (
                        <a key={i} href={`${process.env.REACT_APP_BACKEND_URL}${ph.url}`} target="_blank" rel="noopener noreferrer" className="aspect-square bg-brand-bg overflow-hidden rounded border border-brand">
                          <img src={`${process.env.REACT_APP_BACKEND_URL}${ph.url}`} alt="" className="w-full h-full object-cover hover:scale-110 transition-transform" />
                        </a>
                      ))}
                    </div>
                  )}
                </div>
                {a.status === "submitted" && (
                  <div className="flex gap-2 shrink-0">
                    <button onClick={() => approve(a)} className="px-3 py-1.5 text-xs bg-emerald-600 text-white hover:bg-emerald-700 flex items-center gap-1" data-testid={`ps-approve-${a.id}`}>
                      <CheckCircle2 size={12} /> Godkend
                    </button>
                    <button onClick={() => reject(a)} className="px-3 py-1.5 text-xs border border-red-300 text-red-700 hover:bg-red-50 flex items-center gap-1" data-testid={`ps-reject-${a.id}`}>
                      <XCircle size={12} /> Afvis
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === "partners" && (
        <div className="space-y-3">
          {partners.length === 0 && (
            <div className="bg-white border border-brand p-8 text-center text-brand-text2">Ingen aktive partnere endnu</div>
          )}
          {partners.map((p) => (
            <div key={p.id} className="bg-white border border-brand p-5">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div>
                  <div className="font-display text-lg text-brand-dark font-semibold">{p.shop_name}</div>
                  <div className="text-sm text-brand-text2">{p.shop_address}, {p.shop_zip} {p.shop_city}</div>
                  <div className="text-xs text-brand-text2 mt-1">{p.business_type} · {p.opening_hours}</div>
                  <div className="text-xs text-brand-text2 mt-2 flex items-center gap-2 flex-wrap">
                    Kapacitet:{" "}
                    <strong className={Number(p.current_parcels || 0) >= Number(p.max_parcels || 50) ? "text-red-600" : ""}>
                      {p.current_parcels || 0} / {p.max_parcels || "—"}
                    </strong>
                    <label className="inline-flex items-center gap-1">
                      Max:
                      <input
                        type="number"
                        min={1}
                        max={5000}
                        defaultValue={p.max_parcels || 50}
                        className="w-16 border border-brand px-1 py-0.5 text-xs"
                        onBlur={(e) => updateCapacity(p, e.target.value)}
                        data-testid={`ps-capacity-${p.id}`}
                      />
                    </label>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`text-xs px-2 py-1 ${p.active ? "bg-emerald-50 text-emerald-700" : "bg-slate-100"}`}>{p.active ? "Aktiv" : "Inaktiv"}</span>
                  <button
                    onClick={() => downloadFlyer(p)}
                    className="px-3 py-1.5 text-xs bg-brand-dark text-white hover:bg-black flex items-center gap-1"
                    data-testid={`ps-flyer-${p.id}`}
                  >
                    <FileDown size={12} /> Flyer (PDF)
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <p className="text-xs text-brand-text2 leading-relaxed bg-brand-bg p-4">
        💡 Når en kunde vælger pakkeshop-levering i booking-flowet, kan de vælge mellem alle aktive partnere i deres område. Shop-portalen ligger på <code>/pakkeshop</code> (PIN-beskyttet).
      </p>
    </div>
  );
}
